import { ReactElement, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Icon } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useThemeContext } from 'hooks/context/useThemeContext'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePendingApprovalDetails } from 'hooks/usePendingApprovalDetails'
import { contactCaseCountAtom } from 'pages/Contacts/atoms/contactAtoms'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerModels } from 'pages/Customers/helpers/types'
import { isProduction } from 'utils/environment'

export type SidebarLinkItem = {
  testId: string
  to: string
  title: string
  icon: ReactElement
  requiresVerification: boolean
  isHidden?: boolean
  featureName?: string
  highlight?: number
}

export type SidebarLink = SidebarLinkItem & {
  subItems?: SidebarLinkItem[]
}

export const useSidebarLinks = () => {
  const { darkMode } = useThemeContext()

  const contactCaseCount = useAtomValue(contactCaseCountAtom)
  const customerModel = useAtomValue(customerModelAtom)
  const { accountCapabilities, isVerifiedBusinessAccount, isDeveloperAccount, currentAccount } =
    useAccountsContext()

  const { t } = useTranslation()
  const {
    enableUsLaunchRestrictions,
    enableRewards,
    enableEarn,
    enableEpcV2CustomerCreation,
    enableEpcV2RelianceCustomerCreation,
    enableConsolidatedPayments,
  } = useFeatureFlags()
  const embeddedEnabled = enableEpcV2CustomerCreation && customerModel === CustomerModels.EMBEDDED
  const relianceEnabled =
    enableEpcV2RelianceCustomerCreation && customerModel === CustomerModels.RELIANCE
  const permissions = useMappedRoles()
  const canDisable = !isProduction && isDeveloperAccount ? false : !isVerifiedBusinessAccount

  const currentAccountReference = currentAccount?.reference || ''
  const { numberOfPendingApprovals } = usePendingApprovalDetails(
    currentAccountReference,
    !!currentAccountReference
  )

  const links: SidebarLink[] = useMemo(() => {
    return [
      ...(!isDeveloperAccount
        ? [
            {
              to: `${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`,
              title: t('wallets.title'),
              icon: <Icon name="Wallet" />,
              requiresVerification: canDisable,
              isHidden: !permissions.wallets.canView,
              testId: 'sidebar-wallets',
            },
          ]
        : [
            {
              to: '/',
              title: t('home'),
              icon: <Icon name="House" />,
              requiresVerification: false,
              testId: 'sidebar-home',
            },
          ]),
      ...(isDeveloperAccount
        ? [
            {
              to: `${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`,
              title: t('wallets.title'),
              icon: <Icon name="Wallet" />,
              requiresVerification: canDisable,
              isHidden: !permissions.wallets.canView,
              testId: 'sidebar-wallets',
            },
          ]
        : !enableConsolidatedPayments
          ? [
              {
                to: PathNames.TRANSACTION_HISTORY,
                title: t('wallets.transactionHistory'),
                icon: <Icon name="ListMagnifyingGlass" />,
                requiresVerification: canDisable,
                isHidden: !permissions.wallets.canView,
                testId: 'sidebar-transaction-history',
              },
            ]
          : []),
      ...(!enableConsolidatedPayments && isDeveloperAccount
        ? [
            {
              to: PathNames.TRANSACTION_HISTORY,
              title: t('wallets.transactionHistory'),
              icon: <Icon name="ListMagnifyingGlass" />,
              requiresVerification: canDisable,
              isHidden: !permissions.wallets.canView,
              testId: 'sidebar-transaction-history',
            },
          ]
        : []),
      ...(enableConsolidatedPayments
        ? [
            {
              title: t('approvals.payments'),
              to: PathNames.PAYMENTS,
              testId: 'sidebar-payments',
              isHidden: !permissions.payments.canView && !permissions.wallets.canView,
              icon: <Icon name="CreditCard" />,
              requiresVerification: canDisable,
            },
          ]
        : [
            {
              title: t('sidebar.cryptoPayments'),
              to: `${PathNames.PAYMENTS}${PathNames.CRYPTO}`,
              testId: 'sidebar-crypto-payments',
              isHidden: !permissions.payments.canView,
              icon: <Icon name="CreditCard" />,
              featureName: 'payments',
              requiresVerification: canDisable,
            },
            ...(!enableUsLaunchRestrictions
              ? [
                  {
                    title: t('sidebar.channelPayments'),
                    to: `${PathNames.PAYMENTS}${PathNames.CHANNEL}`,
                    testId: 'sidebar-channel-payments',
                    icon: <Icon name="QrCode" />,
                    isHidden: !permissions.payments.canView,
                    featureName: 'payments',
                    requiresVerification: canDisable,
                  },
                ]
              : []),
          ]),
      {
        to: PathNames.MISSING_INFORMATION,
        icon: <Icon name="UserCirclePlus" />,
        requiresVerification: false,
        title: t('contacts.missingInformation'),
        testId: 'sidebar-missing-contacts',
        highlight: contactCaseCount,
      },
      {
        to: PathNames.MASS_PAYOUTS,
        icon: <Icon name="BoxArrowUp" />,
        requiresVerification: false,
        title: t('massPayouts.massPayouts'),
        testId: 'sidebar-mass-payouts',
      },
      {
        to: PathNames.CUSTOMERS,
        icon: <Icon name="Storefront" />,
        requiresVerification: false,
        title: t('customers.sidebarTitle'),
        testId: 'sidebar-customers',
      },
      {
        to: PathNames.CUSTOMERS_IN_FLIGHT,
        icon: <Icon name="Storefront" />,
        requiresVerification: false,
        title: t('customers.sidebarTitleV2'),
        testId: 'sidebar-customers-v2',
        isHidden: !embeddedEnabled && !relianceEnabled,
        subItems: [
          {
            testId: 'sidebar-customers-in-flight',
            to: PathNames.CUSTOMERS_IN_FLIGHT,
            title: t('inFlight'),
            icon: <Icon name="Storefront" />,
            requiresVerification: false,
          },
          {
            testId: 'sidebar-live-customers',
            to: PathNames.LIVE_CUSTOMERS,
            title: t('live'),
            icon: <Icon name="Storefront" />,
            requiresVerification: false,
          },
        ],
      },
      {
        to: `${PathNames.APPROVALS}`,
        title: t('approvals.title'),
        icon: <Icon name="Timer" />,
        requiresVerification: false,
        testId: 'sidebar-approvals',
        highlight: numberOfPendingApprovals,
      },
      ...(enableRewards && !enableEarn
        ? [
            {
              to: PathNames.REWARDS,
              title: t('rewards.title'),
              icon: <Icon name="SketchLogo" />,
              requiresVerification: false,
              isHidden: !(
                accountCapabilities?.isEarningRewardsCapabilityEnabled &&
                permissions.rewards.canView
              ),
              testId: 'sidebar-rewards',
            },
          ]
        : []),
      ...(enableEarn
        ? [
            {
              to: PathNames.EARN,
              title: t('earn.title'),
              icon: <Icon name="SketchLogo" />,
              requiresVerification: false,
              isHidden: !(
                accountCapabilities?.isEarningRewardsCapabilityEnabled && permissions.earn.canView
              ),
              testId: 'sidebar-earn',
            },
          ]
        : []),
      {
        to: PathNames.CONTACTS,
        title: t('contacts.title'),
        icon: <Icon name="AddressBook" />,
        requiresVerification: false,
        isHidden: !permissions.contacts.canView,
        testId: 'sidebar-crypto-contacts',
      },

      {
        to: PathNames.INTEGRATIONS,
        title: t('integrations.title'),
        icon: <Icon name="Code" />,
        requiresVerification: false,
        isHidden: !permissions.integrations.canView,
        testId: 'sidebar-integrations',
      },
    ]
  }, [
    isDeveloperAccount,
    permissions,
    canDisable,
    enableConsolidatedPayments,
    enableUsLaunchRestrictions,
    enableRewards,
    enableEarn,
    embeddedEnabled,
    relianceEnabled,
    numberOfPendingApprovals,
    contactCaseCount,
    accountCapabilities,
  ])

  const footerLink = useMemo(() => {
    return {
      title: 'BVNK',
      logoUrl: darkMode
        ? 'https://assets.bvnk.com/logo_bvnk_light.svg'
        : 'https://assets.bvnk.com/logo_bvnk_dark.svg',
      to: `${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`,
      url: `${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`,
    }
  }, [darkMode])

  return {
    links,
    footerLink,
  }
}
